import { useSubscriptionContext } from "@/hooks/use-subscription";
import { PlansType, RecurringType } from "@/lib/models/plan";
import { Price } from "@/lib/models/stripe/price";
import { PLANS } from "@/lib/plansFeatures";
import { Utils } from "@/lib/utils";
import { Check, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { PricingCard } from "./pricing-card";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";

interface PricingTableProps {
      onSubscribe: (plansType: PlansType) => void;
}

export default function PricingTable({ onSubscribe }: PricingTableProps) {
      const t = useTranslations('pricing_table');
      const { recurring, setRecurring, currency, setCurrency, currentUserSubscription, currentUserPlan } = useSubscriptionContext();
      const [loadingPrices, setLoadingPrices] = useState(true);
      const [prices, setPrices] = useState<Price[]>([]);



      const getContent = (plan: PlansType, feature: string) => {
            if (plan in PLANS) {
                  const planFeatures = PLANS[plan as keyof typeof PLANS].features[feature as keyof typeof PLANS.free.features];

                  if (typeof planFeatures === 'number') {
                        return t(feature + '_description', { number: planFeatures });
                  }

                  if (typeof planFeatures === 'boolean') {
                        return planFeatures ? <Check className="h-5 w-5 text-kiwilog" /> : <X className="h-5 w-5 text-muted-foreground/30" />;
                  } else if (planFeatures === 'none') {
                        return <X className="h-5 w-5 text-muted-foreground/30" />
                  }

                  return t(planFeatures);
            }
            return null;
      }

      useEffect(() => {
            setLoadingPrices(true);
            Utils.http.fetchData<Price[]>('/api/p/prices').then(prices => {
                  setPrices(prices);
            }).finally(() => setLoadingPrices(false));
      }, []);

      return (
            <div className="py-4 flex flex-col items-center justify-center">

                  <div className="flex">
                        <Tabs defaultValue={recurring} onValueChange={(value) => setRecurring(value as RecurringType)}>
                              <TabsList>
                                    <TabsTrigger value="month">{t('monthly')}</TabsTrigger>
                                    <TabsTrigger value="year">{t('yearly')}</TabsTrigger>
                              </TabsList>
                        </Tabs>
                        <Separator orientation="vertical" className="h-4" />
                        <Select defaultValue={currency} onValueChange={setCurrency}>
                              <SelectTrigger className="w-[90px] border-0 shadow-none">
                                    <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                    <SelectGroup>
                                          <SelectItem value="brl">R$</SelectItem>
                                          <SelectItem value="usd">US$</SelectItem>
                                    </SelectGroup>
                              </SelectContent>
                        </Select>
                  </div>
                  <span className="text-sm text-muted-foreground mb-8">{t('anual_yearly_description')}</span>

                  <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
                        <PricingCard type="free" plan={PLANS.free} onSubscribe={onSubscribe} />
                        <PricingCard type="starter" plan={PLANS.starter} onSubscribe={onSubscribe} />
                        <PricingCard type="pro" plan={PLANS.pro} onSubscribe={onSubscribe} />
                  </div>

                  <h2 className="text-3xl font-bold m-8">{t('compare_our_plans')}</h2>
                  <Card className="w-full">
                        <CardContent>
                              <div className="flex justify-center" id="table_features">
                                    <Table>
                                          <TableHeader>
                                                <TableRow>
                                                      <TableHead><div className="text-left">{t('features')}</div></TableHead>
                                                      <TableHead><div className="text-center">{t('free')}</div></TableHead>
                                                      <TableHead><div className="text-center">{t('starter')}</div></TableHead>
                                                      <TableHead><div className="text-center">{t('pro')}</div></TableHead>
                                                </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                                {Object.keys(PLANS.free.features).map((feature) => (
                                                      <TableRow key={feature}>
                                                            <TableCell><div className="flex justify-start">{t(feature)}</div></TableCell>
                                                            <TableCell><div className="flex justify-center">{getContent('free', feature)}</div></TableCell>
                                                            <TableCell><div className="flex justify-center">{getContent('starter', feature)}</div></TableCell>
                                                            <TableCell><div className="flex justify-center">{getContent('pro', feature)}</div></TableCell>
                                                      </TableRow>
                                                ))}
                                          </TableBody>
                                          <TableFooter className="bg-transparent">
                                                <TableRow>
                                                      <TableCell>
                                                      </TableCell>
                                                      <TableCell>
                                                            <Button size="sm" className="bg-kiwilog hover:bg-kiwilog/90" onClick={() => onSubscribe('free')} >
                                                                  {t('subscribe')}
                                                            </Button>
                                                      </TableCell>
                                                      <TableCell>
                                                            <Button size="sm" className="bg-kiwilog hover:bg-kiwilog/90" onClick={() => onSubscribe('starter')}>
                                                                  {t('subscribe')}
                                                            </Button>
                                                      </TableCell>
                                                      <TableCell>
                                                            <Button size="sm" className="bg-kiwilog hover:bg-kiwilog/90" onClick={() => onSubscribe('pro')}>
                                                                  {t('subscribe')}
                                                            </Button>
                                                      </TableCell>
                                                </TableRow>
                                          </TableFooter>
                                    </Table>
                              </div>
                        </CardContent>
                  </Card>
            </div>
      );
} 